import React from "react";
import { useLanguage } from "../i18n/LanguageContext";

const LANGS = [
  { code: "fi", flag: "🇫🇮", label: "FI" },
  { code: "ee", flag: "🇪🇪", label: "EE" },
  { code: "se", flag: "🇸🇪", label: "SE" },
  { code: "en", flag: "🇬🇧", label: "EN" },
  { code: "ru", flag: "🇷🇺", label: "RU" },
];

/**
 * FlagSvg — cross-platform SVG flag icons for desktop where emoji flags
 * often fall back to plain regional-indicator letters ("FI", "SE", "EE")
 * and would otherwise duplicate the label.
 */
const FlagSvg = ({ code }) => {
  const size = { width: 16, height: 12 };
  const common = {
    ...size,
    viewBox: "0 0 18 12",
    xmlns: "http://www.w3.org/2000/svg",
    style: { display: "block", flexShrink: 0 },
    "aria-hidden": true,
  };

  if (code === "fi") {
    // Finland: white with blue Nordic cross
    return (
      <svg {...common}>
        <rect width="18" height="12" fill="#ffffff" />
        <rect x="5" width="3" height="12" fill="#003580" />
        <rect y="4.5" width="18" height="3" fill="#003580" />
      </svg>
    );
  }
  if (code === "ee") {
    // Estonia: blue / black / white horizontal
    return (
      <svg {...common}>
        <rect width="18" height="4" y="0" fill="#0072CE" />
        <rect width="18" height="4" y="4" fill="#000000" />
        <rect width="18" height="4" y="8" fill="#ffffff" />
      </svg>
    );
  }
  if (code === "se") {
    // Sweden: blue with yellow Nordic cross
    return (
      <svg {...common}>
        <rect width="18" height="12" fill="#006AA7" />
        <rect x="5" width="3" height="12" fill="#FECC00" />
        <rect y="4.5" width="18" height="3" fill="#FECC00" />
      </svg>
    );
  }
  if (code === "en") {
    // United Kingdom flag (Union Jack, simplified)
    return (
      <svg {...common}>
        <rect width="18" height="12" fill="#012169" />
        {/* white diagonals */}
        <polygon points="0,0 3,0 18,10 18,12 15,12 0,2" fill="#ffffff" />
        <polygon points="18,0 15,0 0,10 0,12 3,12 18,2" fill="#ffffff" />
        {/* red diagonals */}
        <polygon points="0,0 1.5,0 18,11 18,12 16.5,12 0,1" fill="#C8102E" />
        <polygon points="18,0 16.5,0 0,11 0,12 1.5,12 18,1" fill="#C8102E" />
        {/* white cross */}
        <rect x="7" width="4" height="12" fill="#ffffff" />
        <rect y="4" width="18" height="4" fill="#ffffff" />
        {/* red cross */}
        <rect x="7.75" width="2.5" height="12" fill="#C8102E" />
        <rect y="4.75" width="18" height="2.5" fill="#C8102E" />
      </svg>
    );
  }
  if (code === "ru") {
    // Russia: white / blue / red horizontal
    return (
      <svg {...common}>
        <rect width="18" height="4" y="0" fill="#ffffff" />
        <rect width="18" height="4" y="4" fill="#0039A6" />
        <rect width="18" height="4" y="8" fill="#D52B1E" />
      </svg>
    );
  }
  return null;
};

/**
 * LanguageSwitcher — compact FI / EE / SE picker.
 * variant "desktop" | "mobile" | "legal"
 * Desktop uses SVG flags (cross-platform safe). Mobile keeps emoji flags.
 */
export const LanguageSwitcher = ({ variant = "desktop" }) => {
  const { lang, setLang } = useLanguage();
  const useEmoji = variant === "mobile";

  return (
    <div
      data-testid={`lang-switcher-${variant}`}
      className={`inline-flex items-center gap-0.5 border-2 border-[#1f1f1f] bg-black/60 backdrop-blur px-1 py-1 ${
        variant === "mobile" ? "w-full justify-around" : ""
      }`}
    >
      {LANGS.map((l) => {
        const active = lang === l.code;
        return (
          <button
            key={l.code}
            type="button"
            onClick={() => setLang(l.code)}
            data-testid={`lang-btn-${l.code}`}
            aria-pressed={active}
            aria-label={`Vaihda kieli: ${l.label}`}
            className={`relative flex items-center gap-1.5 px-2 py-1 font-display font-bold text-xs tracking-widest uppercase transition-colors ${
              active
                ? "text-[#FFEA00]"
                : "text-[#A3A3A3] hover:text-white"
            }`}
          >
            {useEmoji ? (
              <span aria-hidden="true" className="text-sm leading-none">
                {l.flag}
              </span>
            ) : (
              <FlagSvg code={l.code} />
            )}
            <span>{l.label}</span>
            {active && (
              <span
                aria-hidden="true"
                className="absolute left-1 right-1 -bottom-px h-[2px] bg-[#FFEA00]"
              />
            )}
          </button>
        );
      })}
    </div>
  );
};
